import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Trans } from '@lingui/react/macro';
import { z } from 'zod';
import { useCurrentUser } from '@/hooks/queries/auth.ts';
import { PageContainer } from '@/components/page-container.tsx';
import { EmailNotSetWarning } from './+components/email-not-set-warning.tsx';
import { KpiRow } from './+components/dashboard/kpi-row.tsx';
import { LoadCurve } from './+components/dashboard/load-curve.tsx';
import { MonthlyEnergyChart } from './+components/dashboard/monthly-energy-chart.tsx';
import { ParksMap } from './+components/dashboard/parks-map.tsx';
import { PeakHeadroom } from './+components/dashboard/peak-headroom.tsx';
import { RecentSessions } from './+components/dashboard/recent-sessions.tsx';
import { DashboardRefreshButton } from './+components/dashboard/refresh-button.tsx';
import { TopUsers } from './+components/dashboard/top-users.tsx';
import { StripeResultDialogs } from './+components/stripe-result-dialogs.tsx';

const homeSearchSchema = z.object({
  stripe_success: z.boolean().optional(),
  stripe_error: z.boolean().optional(),
});

export const Route = createFileRoute('/_logged/')({
  component: HomePage,
  validateSearch: homeSearchSchema,
});

function HomePage() {
  const { stripe_success, stripe_error } = Route.useSearch();
  const navigate = useNavigate();
  const user = useCurrentUser();

  const showEmailWarning = !user.email;

  return (
    <>
      <StripeResultDialogs
        success={stripe_success ?? false}
        error={stripe_error ?? false}
        onClose={() => navigate({ to: '/', search: {} })}
      />
      <PageContainer>
        <div className='flex items-start justify-between gap-4'>
          <header className='space-y-1'>
            <p className='text-muted-foreground text-xs font-medium tracking-widest uppercase'>
              <Trans comment='Eyebrow line above the home dashboard title'>Overview</Trans>
            </p>
            <h1 className='text-3xl font-semibold tracking-tight'>
              <Trans comment='Home dashboard page title'>Dashboard</Trans>
            </h1>
            <p className='text-muted-foreground text-sm'>
              <Trans comment='Subtitle under the home dashboard title'>
                All your parks at a glance.
              </Trans>
            </p>
          </header>
          <DashboardRefreshButton />
        </div>
        {showEmailWarning ?
          <EmailNotSetWarning user={user} />
        : null}
        <KpiRow />
        <LoadCurve />
        <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
          <ParksMap />
          <MonthlyEnergyChart />
          <PeakHeadroom />
          <TopUsers />
          <RecentSessions />
        </div>
      </PageContainer>
    </>
  );
}
